import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLES = [
  "Founder / C-Suite",
  "Technology Leader (VP, Director, Head of)",
  "Senior Engineer / Architect",
  "Digital Marketing Leader",
  "Other senior practitioner",
];

function WaitlistForm() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", role: "", focus: "", link: "" });
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, {
        email: form.email,
        first_name: form.first_name,
        last_name: form.last_name,
        role: form.role,
        focus: form.focus,
        link: form.link || null,
      });
      setJoined(true);
      toast.success("You're on the waitlist. We'll review your application and be in touch.");
    } catch (err) {
      if (err.response?.status === 409) {
        setJoined(true);
        toast.info("This email is already on the waitlist.");
      } else if (err.response?.status === 422) {
        toast.error("Please complete every required field (a sentence or two about your work).");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (joined) {
    return (
      <div
        className="max-w-xl p-5 bg-white border border-aix-cyan/50 rounded-lg shadow-xl"
        data-testid="waitlist-success-message"
      >
        <p className="text-sm text-aix-cyandeep font-semibold tracking-wide">
          You're on the waitlist — your application is in review. Watch your inbox.
        </p>
      </div>
    );
  }

  const inputCls =
    "w-full bg-slate-50 border border-slate-200 rounded px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-aix-cyan transition-colors";

  return (
    <div className="max-w-xl">
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-3 p-4 bg-white border border-slate-300 rounded-lg shadow-xl"
        data-testid="waitlist-form"
      >
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            required
            value={form.first_name}
            onChange={set("first_name")}
            placeholder="First name"
            className={`${inputCls} flex-1`}
            data-testid="waitlist-first-name-input"
          />
          <input
            type="text"
            required
            value={form.last_name}
            onChange={set("last_name")}
            placeholder="Last name"
            className={`${inputCls} flex-1`}
            data-testid="waitlist-last-name-input"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            required
            value={form.email}
            onChange={set("email")}
            placeholder="Work email"
            className={`${inputCls} flex-grow`}
            data-testid="waitlist-email-input"
          />
          <select
            required
            value={form.role}
            onChange={set("role")}
            className={`${inputCls} sm:w-56 cursor-pointer ${form.role ? "" : "text-slate-400"}`}
            data-testid="waitlist-role-select"
          >
            <option value="" disabled>
              Your role
            </option>
            {ROLES.map((r) => (
              <option key={r} value={r} className="text-slate-900">
                {r}
              </option>
            ))}
          </select>
        </div>
        <textarea
          required
          minLength={20}
          rows={3}
          value={form.focus}
          onChange={set("focus")}
          placeholder="What are you building or shipping with AI right now? A sentence or two is enough."
          className={`${inputCls} resize-none`}
          data-testid="waitlist-focus-input"
        />
        <input
          type="text"
          value={form.link}
          onChange={set("link")}
          placeholder="Link to your work — LinkedIn, GitHub, company site (optional)"
          className={inputCls}
          data-testid="waitlist-link-input"
        />
        <button
          type="submit"
          disabled={loading}
          className="bg-aix-navy hover:bg-[#16407F] disabled:opacity-60 text-white text-sm font-semibold tracking-wide px-6 py-3.5 rounded transition-colors shadow-[0_0_20px_rgba(42,174,228,0.25)]"
          data-testid="waitlist-submit-button"
        >
          {loading ? "Submitting..." : "Join Waitlist"}
        </button>
      </form>
      <p className="text-xs text-slate-500 mt-2.5 flex items-center gap-2">
        <Lock className="w-3 h-3" />
        <span>Membership is curated — what you are shipping matters more than your title. Monthly Zoom roundtables.</span>
      </p>
    </div>
  );
}

export default function Hero() {
  return (
    <section
      id="hero"
      className="relative border-b border-slate-200"
      data-testid="hero-section"
    >
      {/* FULL-WIDTH MASTHEAD — flush under the menu */}
      <div
        className="relative overflow-hidden h-[420px] md:h-[520px]"
        data-testid="hero-image-banner"
      >
        <img
          src="https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHx2aWRlbyUyMGNvbmZlcmVuY2UlMjBjYWxsfGVufDB8fHx8MTc5MjAxMjgxMXww&ixlib=rb-4.1.0&q=85"
          alt="Members on a live video roundtable sharing their screens and code"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#071C3E]/85 via-[#071C3E]/45 to-transparent"></div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-6 h-full flex flex-col">
          <div className="relative pt-6 md:pt-8 self-start">
            <div className="absolute -inset-6 bg-white/55 blur-2xl rounded-full pointer-events-none"></div>
            <img
              src="/assets/aix-logo.png"
              alt="AI Advantage Exchange monogram"
              className="relative h-16 md:h-24 w-auto drop-shadow-[0_0_14px_rgba(255,255,255,0.95)]"
              data-testid="hero-logo-image"
            />
          </div>

          <ul className="space-y-3 mt-auto pb-10 md:pb-12 aix-fade-2" data-testid="hero-benefits">
            {["REAL AI WORK", "CANDID DISCUSSION", "VETTED MEMBERS", "MONTHLY ROUNDTABLES", "PROJECT EXCHANGE"].map((title) => (
              <li key={title} className="flex items-center gap-4">
                <span className="w-3 h-3 bg-aix-cyan shadow-[0_0_8px_#2AAEE4] shrink-0"></span>
                <span className="font-display font-bold tracking-tight text-white uppercase text-base md:text-xl drop-shadow-[0_1px_6px_rgba(4,8,18,0.8)]">
                  {title}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* HEADLINE + FORM */}
      <div className="relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(42,174,228,0.12)_0%,transparent_60%)] pointer-events-none"></div>
        <div className="max-w-6xl mx-auto px-6 relative z-10 pt-14 pb-20 md:pt-16 md:pb-24">
        <h1
          className="font-display text-[clamp(42px,6vw,80px)] font-black tracking-tight leading-[1.02] text-slate-900 max-w-6xl mb-8"
          data-testid="hero-headline"
        >
          <span className="aix-ln"><span>CURATED AI LEADERS</span></span>
          <span className="aix-ln"><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0E2A5C] via-[#1D63C9] to-[#2AAEE4]">SHIPPING BLEEDING-EDGE</span></span>
          <span className="aix-ln"><span>SYSTEMS.</span></span>
        </h1>

        <div id="apply" className="aix-fade-2 scroll-mt-28 mt-10">
          <WaitlistForm />
        </div>
        </div>
      </div>
    </section>
  );
}
